import type { ComponentProps } from 'react'

import { cva, type VariantProps } from 'class-variance-authority'

import { Icon } from '@/shared/components/icon'
import { Render } from '@/shared/components/render-if'

const toastVariants = cva(
  'group flex gap-4 w-full items-center space-between rounded-lg ring-1 shadow-md ring-black/5 md:max-w-lg p-4 text-sm font-medium',
  {
    variants: {
      variant: {
        info: 'bg-sky-200 shadow-sm [&_button]:hover:bg-sky-200',
        success: 'bg-green-200 shadow-sm [&_button]:hover:bg-green-200',
        error: 'bg-red-200 shadow-sm [&_button]:hover:bg-red-200',
        warning: 'bg-orange-200 [&_button]:hover:bg-orange-200',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
)

interface IToastProps extends VariantProps<typeof toastVariants> {
  id: string | number
  message: string
  closeButton?: boolean
  onClose?: VoidFunction
  dismiss: VoidFunction
}

const iconePorVariant: Record<NonNullable<IToastProps['variant']>, ComponentProps<typeof Icon>['name']> = {
  info: 'info',
  success: 'circle-check',
  warning: 'triangle-alert',
  error: 'circle-alert',
}

export const Toast = ({ variant = 'info', message, closeButton = true, onClose, dismiss }: Readonly<IToastProps>) => {
  return (
    <div className={toastVariants({ variant })}>
      <Render.If condition={variant}>
        {(_variant) => (
          <div>
            <Icon name={iconePorVariant[_variant]} />
          </div>
        )}
      </Render.If>
      <p>{message}</p>
      <Render.If condition={closeButton}>
        {(_button) => (
          <button
            className="rounded-full p-1.5"
            onClick={() => {
              onClose?.()
              dismiss()
            }}
          >
            <Icon name="circle-close" />
          </button>
        )}
      </Render.If>
    </div>
  )
}
